import {ApiResponse, PaginatedApiReponse} from '../models/ApiResponse';
import {Microlesson} from '../models/Microlessons/Microlesson';
import {MicroreelGroup} from '../models/Microreels/MicroreelGroup';
import {
  MicroreelGenerationModel,
  MicroreelSlideGenerationModel,
  MicroreelVideoGenerationModel,
  MicroreelsGenerationStatusResult,
} from '../models/Microreels/MicroreelGenerationModel';
import {apiClient} from './baseApiClient';
import {ProjectFilter} from '../models/Projects/Project';

// The generate endpoints answer only after probing the source video and
// calling the AI service, whose own HttpClient waits 8 minutes. Aborting
// earlier drops the response that carries the job id, and without it
// generation_status can no longer be asked about this generation.
const GENERATE_REQUEST_TIMEOUT_MS = 600000;

class MicroreelsApi {
  public async getMicroreelsList(
    organizationId: string,
    page: number,
    pageSize: number,
    search?: string,
    filter?: ProjectFilter,
  ): Promise<PaginatedApiReponse<MicroreelGroup>> {
    return apiClient.post(`/microreel/${organizationId}/list`, {
      page,
      pageSize,
      orderBy: '',
      search,
      orderAsk: false,
      ...filter,
    });
  }

  public async generateFromPrompt(
    organizationId: string,
    model: MicroreelGenerationModel,
  ): Promise<ApiResponse<any>> {
    return apiClient.post(
      `/microreel/${organizationId}/generate_from_prompt`,
      model,
      {timeout: GENERATE_REQUEST_TIMEOUT_MS},
    );
  }

  public async generateFromSlide(
    organizationId: string,
    model: MicroreelSlideGenerationModel,
  ): Promise<ApiResponse<any>> {
    return apiClient.post(
      `/microreel/${organizationId}/generate_from_slide`,
      model,
      {timeout: GENERATE_REQUEST_TIMEOUT_MS},
    );
  }

  public async generateFromVideo(
    organizationId: string,
    model: MicroreelVideoGenerationModel,
  ): Promise<ApiResponse<any>> {
    return apiClient.post(
      `/microreel/${organizationId}/generate_from_video`,
      model,
      {timeout: GENERATE_REQUEST_TIMEOUT_MS},
    );
  }

  public async cancelGeneration(
    organizationId: string,
  ): Promise<ApiResponse<any>> {
    return apiClient.post(`/microreel/${organizationId}/cancel_generation`);
  }

  public async getMicroreelsLibrary(
    organizationId: string,
    page: number,
    pageSize: number,
    isGradable?: boolean,
    search?: string,
  ): Promise<PaginatedApiReponse<Microlesson>> {
    return apiClient.post(`/microreel/${organizationId}/library`, {
      page,
      pageSize,
      search,
      isGradable,
    });
  }

  public async copyMicroreelsToCourse(
    organizationId: string,
    courseId: string,
    folderId: string,
    microreelsIds: string[],
  ): Promise<ApiResponse<any>> {
    return apiClient.post(`/microreel/${organizationId}/copy_to_course`, {
      courseId,
      folderId,
      microreelsIds,
    });
  }

  public async checkGenerationAvailability(
    organizationId: string,
  ): Promise<ApiResponse<boolean>> {
    const response = await apiClient.get<{isAvailable: boolean}>(
      `/microreel/${organizationId}/available_generation`,
    );

    return {
      success: true,
      data: response.data.isAvailable,
    };
  }

  // Status (numeric enum) of one generation job when generationId is given,
  // otherwise of the requester's most recent PromptToLessonJob of any kind.
  public async getGenerationStatus(
    organizationId: string,
    generationId?: string,
  ): Promise<ApiResponse<MicroreelsGenerationStatusResult>> {
    const response: any = await apiClient.get(
      `/microreel/${organizationId}/generation_status`,
      {params: generationId ? {generationId} : undefined},
    );
    return {
      success: response?.success ?? false,
      data: {
        status: response?.data?.status,
        microreelsId: response?.data?.microreelsId ?? undefined,
      },
      errors: response?.errors,
    };
  }
}

export default MicroreelsApi;
